import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  type QueryConstraint,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  type WithFieldValue,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { Org } from "@/types/firestore";
import type { Studio } from "@/types/firestore";
import type { OrgUser } from "@/types/firestore";
import type { EndUser } from "@/types/firestore";
import type { FeeSchedule } from "@/types/firestore";
import type { UsageEvent } from "@/types/firestore";
import type { BillingReport } from "@/types/firestore";
import type { AuditLogEntry } from "@/types/firestore";
import type { InviteLink } from "@/types/firestore";
import type { Group } from "@/types/firestore";
import type { CommunicationTemplate } from "@/types/communications";
import type { CommunicationCampaign } from "@/types/communications";
import type { CommunicationAutomation } from "@/types/communications";
import type { CommunicationSegment } from "@/types/communications";
import type { CommunicationPreferences } from "@/types/communications";
import type { TimelineEvent } from "@/types/communications";
import type { AdminNote } from "@/types/communications";
import type { OrgCommunicationConfig } from "@/types/communications";
import type { AutomationJob } from "@/types/communications";
import type { Resource } from "@/types/firestore";
import type { DripSequence } from "@/types/drip";
import type { DripEnrollment } from "@/types/drip";
import type { PlatformMaintenanceSettings } from "@/types/maintenance";
import type { OrgIntegrationToken } from "@/types/integrations";
import type { CivicMeta } from "@/types/civic";
import type { PromptVersion } from "@/types/civic";
import type { AnalyticsDaily, ConversationRecord, ContentGap } from "@/types/foyer";
import type { WorkshopCatalogItem, Workshop, WorkshopRegistration, WorkshopPublicPage, WorkshopFacilitatorLink } from "@/types/workshops";
import type { Tag } from "@/types/tags";
import type { Contact } from "@/types/contacts";
import type { ContentStory, ContentCtaBlock } from "@/types/content";
import type { McpSendAuthorization } from "@/types/mcp";
import type { AgreementDoc, LedgerEntryData, PeriodDoc, RateCardDoc, StatementDoc } from "@/types/support";
import { normalizeContact } from "@/lib/normalizeContact";
import { normalizeEndUser } from "@/lib/normalizeEndUser";

// ─── Generic Firestore Converter ─────────────────────────────────────────────

function createConverter<T extends DocumentData>(): FirestoreDataConverter<T> {
  return {
    toFirestore(data: WithFieldValue<T>): DocumentData {
      return data as DocumentData;
    },
    fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): T {
      return snapshot.data(options) as T;
    },
  };
}

// ─── Typed Collection References ─────────────────────────────────────────────

// Studio-specific converter that strips the encrypted API key from client state.
// The encrypted value should never sit in browser memory / React DevTools.
// Existing code checks `studio.pickaxeApiKey ? "Configured" : "Not set"`,
// so we replace the cipher-text with a harmless truthy marker.
const studioConverter: FirestoreDataConverter<Studio> = {
  toFirestore(data: WithFieldValue<Studio>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): Studio {
    const raw = snapshot.data(options);
    return {
      ...raw,
      // Keep truthy/falsy semantics, discard the actual cipher-text
      pickaxeApiKey: raw.pickaxeApiKey ? "[configured]" : "",
    } as Studio;
  },
};

// Normalizing converters — contacts and endUsers have multiple writers and
// partial legacy docs in production, so their fromFirestore routes through
// the normalize-on-read helpers instead of createConverter's bare cast.
// New multi-writer collections should follow this shape, not createConverter.
const contactConverter: FirestoreDataConverter<Contact> = {
  toFirestore(data: WithFieldValue<Contact>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): Contact {
    return normalizeContact(snapshot.data(options));
  },
};

const endUserConverter: FirestoreDataConverter<EndUser> = {
  toFirestore(data: WithFieldValue<EndUser>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): EndUser {
    return normalizeEndUser(snapshot.data(options));
  },
};

export const converters = {
  orgs: createConverter<Org>(),
  studios: studioConverter,
  orgUsers: createConverter<OrgUser>(),
  endUsers: endUserConverter,
  feeSchedules: createConverter<FeeSchedule>(),
  usageEvents: createConverter<UsageEvent>(),
  billingReports: createConverter<BillingReport>(),
  auditLog: createConverter<AuditLogEntry>(),
  inviteLinks: createConverter<InviteLink>(),
  groups: createConverter<Group>(),
  communicationTemplates: createConverter<CommunicationTemplate>(),
  communicationCampaigns: createConverter<CommunicationCampaign>(),
  communicationAutomations: createConverter<CommunicationAutomation>(),
  automationJobs: createConverter<AutomationJob>(),
  communicationSegments: createConverter<CommunicationSegment>(),
  communicationPreferences: createConverter<CommunicationPreferences>(),
  timelineEvents: createConverter<TimelineEvent>(),
  adminNotes: createConverter<AdminNote>(),
  orgCommunicationConfigs: createConverter<OrgCommunicationConfig>(),
  resources: createConverter<Resource>(),
  dripSequences: createConverter<DripSequence>(),
  dripEnrollments: createConverter<DripEnrollment>(),
  platformSettings: createConverter<PlatformMaintenanceSettings>(),
  orgIntegrationTokens: createConverter<OrgIntegrationToken>(),
  workshopCatalog: createConverter<WorkshopCatalogItem>(),
  workshops: createConverter<Workshop>(),
  workshopRegistrations: createConverter<WorkshopRegistration>(),
  workshopPublicPages: createConverter<WorkshopPublicPage>(),
  workshopFacilitatorLinks: createConverter<WorkshopFacilitatorLink>(),
  tags: createConverter<Tag>(),
  contacts: contactConverter,
  contentStories: createConverter<ContentStory>(),
  contentCtaBlocks: createConverter<ContentCtaBlock>(),
  mcpSendAuthorizations: createConverter<McpSendAuthorization>(),
  rateCards: createConverter<RateCardDoc>(),
  supportAgreements: createConverter<AgreementDoc>(),
  supportStatements: createConverter<StatementDoc>(),
  // NOTE: civic converters are intentionally NOT in this map.
  // Civic data lives at studios/{studioId}/civic/... (not a root collection),
  // so typedCollection/typedDoc would silently produce wrong paths.
  // Use civicMetaRef(), civicPromptVersionsRef(), civicPromptVersionRef() exclusively.
} as const;

// ─── Civic Converters (module-private) ───────────────────────────────────────
// Kept private to prevent misuse via typedCollection/typedDoc, which pass the
// key directly to collection(db, name) and would silently target a non-existent
// root collection instead of studios/{studioId}/civic/...
const civicMetaConverter = createConverter<CivicMeta>();
const civicPromptVersionConverter = createConverter<PromptVersion>();

export function typedCollection<T extends keyof typeof converters>(name: T) {
  return collection(db, name).withConverter(
    converters[name] as FirestoreDataConverter<DocumentData>
  );
}

export function typedDoc<T extends keyof typeof converters>(
  collectionName: T,
  docId: string
) {
  return doc(db, collectionName, docId).withConverter(
    converters[collectionName] as FirestoreDataConverter<DocumentData>
  );
}

// ─── Convenience Helpers ─────────────────────────────────────────────────────

export async function getDocument<T extends keyof typeof converters>(
  collectionName: T,
  docId: string
) {
  const ref = typedDoc(collectionName, docId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function queryDocuments<T extends keyof typeof converters>(
  collectionName: T,
  ...constraints: QueryConstraint[]
) {
  const ref = typedCollection(collectionName);
  const q = query(ref, ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── Civic Studio Subcollection Helpers ──────────────────────────────────────
// All paths are under studios/{studioId}/civic/…  (covered by Foundation rule)
//
// civic/meta                      → CivicMeta singleton doc (variables are a map field here)
// civic/meta/versions/{versionId} → PromptVersion subcollection of the meta doc

export function civicMetaRef(studioId: string) {
  return doc(db, "studios", studioId, "civic", "meta").withConverter(
    civicMetaConverter
  );
}

export function civicPromptVersionsRef(studioId: string) {
  return collection(db, "studios", studioId, "civic", "meta", "versions").withConverter(
    civicPromptVersionConverter
  );
}

export function civicPromptVersionRef(studioId: string, versionId: string) {
  return doc(db, "studios", studioId, "civic", "meta", "versions", versionId).withConverter(
    civicPromptVersionConverter
  );
}

// ─── Foyer / CivicOne Analytics Subcollection Helpers ────────────────────────
// All paths are under studios/{studioId}/foyer/data/…
//
// foyer/data                              → singleton doc holding the ingestState field
// foyer/data/analyticsDaily/{YYYY-MM-DD} → daily rollup docs (AnalyticsDaily)
// foyer/data/conversations/{responseId}  → per-conversation records (ConversationRecord)
//
// NOTE: foyer converters are intentionally NOT in the converters map.
// Like civic, these live at a studio subcollection path, not a root collection.
// Use foyerDataRef(), analyticsDailyRef(), conversationsRef() exclusively.

// AnalyticsDaily converter: injects snapshot.id as the `id` field.
const analyticsDailyConverter: FirestoreDataConverter<AnalyticsDaily> = {
  toFirestore(data: WithFieldValue<AnalyticsDaily>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): AnalyticsDaily {
    const d = snapshot.data(options);
    return {
      id: snapshot.id,
      uniqueUsers: (d.uniqueUsers as number) ?? 0,
      totalConversations: (d.totalConversations as number) ?? 0,
      resolvedRate: (d.resolvedRate as number) ?? 0,
      unknownRate: (d.unknownRate as number) ?? 0,
      byTopic: (d.byTopic as Record<string, number>) ?? {},
      kbDocUsage: (d.kbDocUsage as Record<string, number>) ?? {},
      computedAt: (d.computedAt as AnalyticsDaily["computedAt"]) ?? null,
    };
  },
};

// ConversationRecord converter: injects snapshot.id as the `id` field.
const conversationRecordConverter: FirestoreDataConverter<ConversationRecord> = {
  toFirestore(data: WithFieldValue<ConversationRecord>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): ConversationRecord {
    const d = snapshot.data(options);
    return {
      id: snapshot.id,
      userId: (d.userId as string) ?? "",
      model: (d.model as string) ?? "",
      startedAt: (d.startedAt as number) ?? 0,
      questionText: (d.questionText as string) ?? "",
      summary: (d.summary as string) ?? "",
      topic: (d.topic as string) ?? "Uncategorized",
      resolved: (d.resolved as boolean) ?? false,
      unknownAnswer: (d.unknownAnswer as boolean) ?? false,
      tags: Array.isArray(d.tags) ? (d.tags as string[]) : [],
      kbDocIds: Array.isArray(d.kbDocIds) ? (d.kbDocIds as string[]) : [],
      analyzedAt: (d.analyzedAt as ConversationRecord["analyzedAt"]) ?? null,
      analysisStatus: d.analysisStatus === "ok" ? "ok" : "failed",
    };
  },
};

/**
 * Reference to the studios/{studioId}/foyer/data singleton doc.
 * This doc holds the ingestState field (no converter needed — callers read
 * .ingestState directly from the snapshot data).
 */
export function foyerDataRef(studioId: string) {
  return doc(db, "studios", studioId, "foyer", "data");
}

/**
 * Reference to the analyticsDaily subcollection under foyer/data.
 * Each document id is a YYYY-MM-DD string.
 */
export function analyticsDailyRef(studioId: string) {
  return collection(
    db,
    "studios",
    studioId,
    "foyer",
    "data",
    "analyticsDaily"
  ).withConverter(analyticsDailyConverter);
}

/**
 * Reference to the conversations subcollection under foyer/data.
 * Each document id is the Pickaxe responseId.
 */
export function conversationsRef(studioId: string) {
  return collection(
    db,
    "studios",
    studioId,
    "foyer",
    "data",
    "conversations"
  ).withConverter(conversationRecordConverter);
}

// ContentGap converter: injects snapshot.id as the `id` field.
// Module-private — callers use contentGapsRef() exclusively.
const contentGapConverter: FirestoreDataConverter<ContentGap> = {
  toFirestore(data: WithFieldValue<ContentGap>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): ContentGap {
    const d = snapshot.data(options);
    const rawStatus = d.status as string;
    const status: ContentGap["status"] =
      rawStatus === "dismissed" || rawStatus === "addressed" || rawStatus === "applying"
        ? rawStatus
        : "open";
    return {
      id: snapshot.id,
      signature: (d.signature as string) ?? "",
      question: (d.question as string) ?? "",
      variants: Array.isArray(d.variants) ? (d.variants as string[]) : [],
      occurrence: (d.occurrence as number) ?? 0,
      users: (d.users as number) ?? 0,
      topic: (d.topic as string) ?? "Uncategorized",
      tags: Array.isArray(d.tags) ? (d.tags as string[]) : [],
      recommendation: (d.recommendation as string) ?? "",
      downvoted: (d.downvoted as number) ?? 0,
      status,
      computedAt: (d.computedAt as ContentGap["computedAt"]) ?? null,
      appliedDocumentId: (d.appliedDocumentId as string) ?? null,
      appliedName: (d.appliedName as string) ?? null,
      appliedAt: (d.appliedAt as ContentGap["appliedAt"]) ?? null,
      applyingAt: (d.applyingAt as ContentGap["applyingAt"]) ?? null,
    };
  },
};

/**
 * Reference to the contentGaps subcollection under foyer/data.
 * Each document id is an auto-generated Firestore id.
 */
export function contentGapsRef(studioId: string) {
  return collection(
    db,
    "studios",
    studioId,
    "foyer",
    "data",
    "contentGaps"
  ).withConverter(contentGapConverter);
}

export { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, query, where, getDocs, serverTimestamp };

// ─── BiG-Support subcollections ─────────────────────────────────────────────
// periods and ledger live under supportAgreements/{id}; like civic, they are
// reached through these helpers rather than the converters map.
const supportPeriodConverter = createConverter<PeriodDoc>();
const supportLedgerConverter = createConverter<LedgerEntryData>();

export function supportPeriodsRef(agreementId: string) {
  return collection(db, "supportAgreements", agreementId, "periods").withConverter(supportPeriodConverter);
}

export function supportLedgerRef(agreementId: string) {
  return collection(db, "supportAgreements", agreementId, "ledger").withConverter(supportLedgerConverter);
}
